/**
 * 契約プラン一覧 `/subscriptions`。
 *
 * 「どの組織がどのプランで、いま支払えているか」を横断で見る画面。
 * 請求書そのものは `/invoices`、組織単位の詳細は組織詳細タブに置く。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, MoreHorizontal, Pencil, Undo2, XCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { PLAN_CATALOG, SUBSCRIPTION_STATUS, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { CountedList, OpsPlan, SubscriptionSummary } from '../types/api.js';

const routeApi = getRouteApi(RID.subscriptions);

export function SubscriptionsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);

  const [editTarget, setEditTarget] = React.useState<SubscriptionSummary | null>(null);
  const [repriceTarget, setRepriceTarget] = React.useState<SubscriptionSummary | null>(null);
  const [cancelTarget, setCancelTarget] = React.useState<SubscriptionSummary | null>(null);

  /*
   * **サーバーが解釈するものだけを送る。**
   * `GET /ops/subscriptions` は `status` / `q` / `planCode` / `sort` / `order`。
   * `q`（組織名・slug・請求先メール）と `planCode` は API に在るのに画面が
   * 送っておらず、「あの会社の契約」を探すのにカーソルをめくるしかなかった。
   */
  const list = useCursorList<SubscriptionSummary>({
    path: '/subscriptions',
    keyFor: (params) => qk.subscriptions(params),
    filters: {
      status: search.status,
      q: search.q,
      planCode: search.planCode,
      sort: search.sort,
      order: search.order,
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  /** 絞り込みを外した既定（並べ替えはサーバーの既定に戻す）。 */
  const clearedSearch = { sort: 'id', order: 'desc' } as const;

  const columns: Array<DataTableColumn<SubscriptionSummary>> = [
    {
      /*
       * **列 `id` は `sort` の値と一致させる。** DataTable は `sort.columnId` を
       * 列 `id` で引き当てて矢印を出すので、ここが `org` のままだと
       * 「並べ替えているのに、どの列で並んでいるか分からない」表になる。
       */
      id: 'name',
      header: '契約組織',
      sortable: true,
      cell: (s) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: s.organizationId }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {s.organizationName}
        </Link>
      ),
    },
    {
      id: 'plan',
      header: 'プラン',
      cell: (s) => (
        <div className="min-w-0">
          <span className="block truncate">{s.planName}</span>
          <span className="block text-xs text-muted-foreground">
            {s.billingPeriod === 'yearly' ? '年額' : '月額'}
            {s.quantity > 1 ? ` × ${formatCount(s.quantity)}` : ''}
          </span>
        </div>
      ),
    },
    { id: 'status', header: '状態', cell: (s) => <StatusBadge status={s.status} size="sm" dot /> },
    {
      id: 'amount',
      header: '契約金額',
      align: 'right',
      cell: (s) => (
        <div className="space-y-0.5">
          <span className="block tabular">{formatJpy(s.amountJpy)}</span>
          {/*
            予告済みの料金改定。適用日が来るまで `amountJpy` には現れないので、
            ここに出さないと「値上げを予約したか」が一覧から分からない。
          */}
          {s.pendingPriceChange ? (
            <Badge variant="warning" size="sm">
              {formatDateTime(s.pendingPriceChange.effectiveAt)} から{' '}
              {formatJpy(s.pendingPriceChange.amountJpy)}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      // `periodEnd`（= `subscriptions.current_period_end`）でサーバー側ソートできる。
      // 「次に更新が来る契約」から潰すのが更新業務の順序。
      id: 'periodEnd',
      header: '現在の期間',
      hideBelow: 'md',
      sortable: true,
      cell: (s) => `${formatDateTime(s.currentPeriodStart)} 〜 ${formatDateTime(s.currentPeriodEnd)}`,
    },
    {
      id: 'cancelAt',
      header: '解約予定',
      hideBelow: 'lg',
      cell: (s) =>
        s.cancelAt ? (
          <span className="text-warning">
            {formatDateTime(s.cancelAt)}
            {s.cancelAtPeriodEnd ? '（期末）' : ''}
          </span>
        ) : (
          '—'
        ),
    },
    { id: 'createdAt', header: '契約開始', hideBelow: 'lg', cell: (s) => formatDateTime(s.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (s) =>
        canWrite ? (
          <SubscriptionRowActions
            subscription={s}
            onEdit={setEditTarget}
            onReprice={setRepriceTarget}
            onCancel={setCancelTarget}
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="契約プラン"
        description="組織ごとの契約状態。支払い遅延（past_due）はここで拾って督促に回します。"
        actions={<RefetchingDot active={list.query.isFetching && !list.query.isLoading} />}
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="組織名 / slug / 請求先メールで絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: clearedSearch })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(SUBSCRIPTION_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
          {
            key: 'planCode',
            label: 'プラン',
            value: search.planCode,
            // 選択肢は `PLAN_CATALOG`（プランの上限・機能はここが正）から出す。
            options: PLAN_CATALOG.map((p) => ({ value: p.code, label: p.name })),
            onChange: (v) => setSearch({ planCode: v }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(s) => s.id}
        loading={list.query.isLoading}
        sort={{ columnId: search.sort, direction: search.order }}
        onSortChange={(next) => setSearch({ sort: next.columnId as typeof search.sort, order: next.direction })}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="契約"
            onClearFilter={() => void navigate({ search: clearedSearch })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <EditSubscriptionDialog subscription={editTarget} onClose={() => setEditTarget(null)} />
      <RepriceDialog subscription={repriceTarget} onClose={() => setRepriceTarget(null)} />
      <CancelSubscriptionDialog subscription={cancelTarget} onClose={() => setCancelTarget(null)} />
    </>
  );
}

/* -------------------------------------------------------------- 行アクション */

function SubscriptionRowActions({
  subscription,
  onEdit,
  onReprice,
  onCancel,
}: {
  subscription: SubscriptionSummary;
  onEdit: (s: SubscriptionSummary) => void;
  onReprice: (s: SubscriptionSummary) => void;
  onCancel: (s: SubscriptionSummary) => void;
}): React.ReactElement {
  const qc = useQueryClient();

  /*
   * 解約予約の取り消し。**`cancelAtPeriodEnd: false` を送ると
   * `canceled_at` / `ended_at` まで戻る**（サーバー側で
   * `contract.revoke-termination` を積んで `terminate_after` も外す）。
   * これが無いと「やっぱり続けたい」に DB を手で書き換えて応じることになる。
   */
  const revoke = useMutation({
    mutationFn: () =>
      api.patch(`/subscriptions/${subscription.id}`, {
        cancelAtPeriodEnd: false,
        /*
         * **即時解約からの撤回では `status` も戻す。**
         * 即時解約（`status:'canceled'`）はサーバー側で
         * `cancelAtPeriodEnd = false` を必ず書くので、`cancelAtPeriodEnd`
         * だけ送っても `canceled` のまま残り、`canceled_at` / `ended_at` だけが
         * 消えた中途半端な契約になる（＝解約済みなのに終了時刻の無い行）。
         */
        ...(subscription.status === 'canceled' ? { status: 'active' as const } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.subscriptions() }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${subscription.organizationName} の契約操作`}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="truncate">{subscription.organizationName}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onEdit(subscription)}>
          <Pencil className="size-4" aria-hidden />
          契約内容を修正する
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onReprice(subscription)}>
          <Coins className="size-4" aria-hidden />
          単価を改定する
        </DropdownMenuItem>
        {/*
          **即時解約した契約にも撤回の入口を出す。**
          以前は `cancelAtPeriodEnd` が真のときにしか出しておらず、
          即時解約（サーバー側が `cancelAtPeriodEnd = false` を必ず書く）を
          行うと撤回メニューが永久に消えていた。誤操作・請求の行き違いで
          即時解約した場合、`organizations.terminate_after` を外す手段が
          DB 直更新しか無くなり、猶予が過ぎれば Maildir が物理削除される。
          組織詳細の「解約を撤回する」と同じ操作の別入口。
        */}
        {subscription.cancelAtPeriodEnd || subscription.status === 'canceled' ? (
          <DropdownMenuItem onSelect={() => revoke.mutate()} disabled={revoke.isPending}>
            <Undo2 className="size-4" aria-hidden />
            {subscription.cancelAtPeriodEnd ? '解約予約を取り消す' : '解約を撤回する'}
          </DropdownMenuItem>
        ) : null}
        {subscription.status !== 'canceled' ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onSelect={() => onCancel(subscription)}>
              <XCircle className="size-4" aria-hidden />
              契約を解約する
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------ 契約内容の修正 */

/**
 * 契約の手動修正（`PATCH /ops/subscriptions/:id`）。
 *
 * **通常のプラン変更は契約者面（`POST /portal/billing/plan-change`）を通す。**
 * ここは「契約者側の変更ジョブが失敗して契約が中途半端になった」ときの逃げ道で、
 * プランを差し替えると据え置き単価（グランドファザリング）が破棄され、
 * 契約履歴（`contract_events`）にも運営が手で変えた事実が残る。
 */
function EditSubscriptionDialog({
  subscription,
  onClose,
}: {
  subscription: SubscriptionSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [planCode, setPlanCode] = React.useState('');
  const [quantity, setQuantity] = React.useState('1');
  const [billingPeriod, setBillingPeriod] = React.useState<'monthly' | 'yearly'>('monthly');
  const [status, setStatus] = React.useState<SubscriptionSummary['status']>('active');
  const [periodEnd, setPeriodEnd] = React.useState('');

  const plans = useQuery({
    queryKey: qk.plans(),
    queryFn: () => api.get<CountedList<OpsPlan>>('/plans'),
    enabled: subscription !== null,
  });

  React.useEffect(() => {
    if (!subscription) return;
    setPlanCode(subscription.planCode);
    setQuantity(String(subscription.quantity));
    setBillingPeriod(subscription.billingPeriod);
    setStatus(subscription.status);
    setPeriodEnd(subscription.currentPeriodEnd.slice(0, 10));
  }, [subscription]);

  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (!subscription) return Promise.resolve(undefined);
      // 変わった項目だけを送る。全部送ると「触っていない項目の再適用」で
      // 据え置き単価が無用に破棄される（`planId` を送るだけで焼き直しが走る）。
      if (planCode !== subscription.planCode) body.planId = planCode;
      if (Number(quantity) !== subscription.quantity) body.quantity = Number(quantity);
      if (billingPeriod !== subscription.billingPeriod) body.billingPeriod = billingPeriod;
      if (status !== subscription.status) body.status = status;
      if (periodEnd !== subscription.currentPeriodEnd.slice(0, 10)) {
        body.currentPeriodEnd = new Date(`${periodEnd}T00:00:00`).toISOString();
      }
      return api.patch<{ ok: boolean; terminating: boolean }>(
        `/subscriptions/${subscription.id}`,
        body,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions() });
      if (subscription) void qc.invalidateQueries({ queryKey: qk.org(subscription.organizationId) });
      onClose();
    },
  });

  if (!subscription) return null;

  const quantityValue = Number(quantity);
  const quantityInvalid = !Number.isInteger(quantityValue) || quantityValue < 1;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${subscription.organizationName} の契約を修正`}
      description="運営による手動修正です。監査ログと契約履歴に残ります。通常のプラン変更は契約者側の操作を案内してください。"
      submitLabel="修正を保存する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={quantityInvalid}
      onSubmit={() => mut.mutate()}
    >
      <Field
        label="プラン"
        hint="差し替えると据え置き単価（契約時に焼き付けた価格）が破棄され、次回の同期でカタログ価格に揃います。"
      >
        <NativeSelect
          value={planCode}
          onChange={setPlanCode}
          disabled={plans.isLoading}
          options={(plans.data?.items ?? []).map((p) => ({
            value: p.code,
            label: `${p.name}（${p.code}${p.retired ? '・提供終了' : ''}）`,
          }))}
        />
      </Field>

      <Field label="契約枠数" required error={quantityInvalid ? '1 以上の整数で入力してください。' : undefined}>
        <Input
          type="number"
          min={1}
          step={1}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          inputMode="numeric"
        />
      </Field>

      <Field label="請求周期">
        <NativeSelect
          value={billingPeriod}
          onChange={(v) => setBillingPeriod(v as 'monthly' | 'yearly')}
          options={[
            { value: 'monthly', label: '月額' },
            { value: 'yearly', label: '年額' },
          ]}
        />
      </Field>

      <Field
        label="契約状態"
        hint="canceled にすると契約終了処理（データ削除の予定日設定を含む）まで進みます。取り消せません。"
      >
        <NativeSelect
          value={status}
          onChange={(v) => setStatus(v as SubscriptionSummary['status'])}
          options={optionsFrom(SUBSCRIPTION_STATUS, labelOf)}
        />
      </Field>

      <Field label="現在の期間の終了日" hint="請求サイクルのずれを直すときだけ触ってください。">
        <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------------------ 解約 */

/**
 * 解約。**期末解約（予約）と即時解約を明確に分ける。**
 *
 * 即時解約は `status='canceled'` として送られ、サーバーが
 * `contract.terminate` を積む（＝解約後 N 日でのデータ削除が動き出す）。
 * 取り返しがつかないので、組織名の完全一致入力を要求する。
 */
function CancelSubscriptionDialog({
  subscription,
  onClose,
}: {
  subscription: SubscriptionSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [immediate, setImmediate] = React.useState(false);

  React.useEffect(() => {
    if (subscription) setImmediate(false);
  }, [subscription]);

  const mut = useMutation({
    mutationFn: () =>
      api.patch<{ ok: boolean; terminating: boolean }>(`/subscriptions/${subscription?.id ?? ''}`,
        immediate ? { status: 'canceled' } : { cancelAtPeriodEnd: true },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions() });
      onClose();
    },
  });

  if (!subscription) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${subscription.organizationName} の契約を解約`}
      description={
        immediate
          ? '即時解約します。契約終了処理（保存期間の経過後にメールデータを削除する予定日の設定）まで進みます。'
          : `期末（${formatDateTime(subscription.currentPeriodEnd)}）で解約する予約を入れます。予約は後から取り消せます。`
      }
      confirmText={subscription.organizationName}
      confirmLabel={immediate ? '即時解約する' : '期末解約を予約する'}
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onConfirm={() => mut.mutate()}
    >
      <label className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
        <input
          type="checkbox"
          checked={immediate}
          onChange={(e) => setImmediate(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-destructive">期末を待たずに即時解約する</span>
          <span className="block text-xs text-muted-foreground">
            チェックしない場合は期末解約の予約になります（サービスは期間終了まで継続）。
          </span>
        </span>
      </label>
    </ConfirmTextDialog>
  );
}


/* ------------------------------------------------------------ 単価の改定 */

/**
 * 個別契約の単価改定（`POST /ops/billing/subscriptions/:id/reprice`）。
 *
 * **値上げは効力発生日を指定して「予約」にする。** 利用規約 第 11 条
 * （民法 548 条の 4）で「不利益となる変更は効力発生日の 30 日前までに通知する」と
 * 掲示しているので、即時に単価を書き換える経路しか無いと、掲示した手続きを
 * 当社自身が守れない。予約にすると周知だけ先に出て、適用は日次のジョブが行う。
 *
 * 値下げ・カタログ価格への揃え直しは即時でよい（不利益変更ではない）。
 */
function RepriceDialog({
  subscription,
  onClose,
}: {
  subscription: SubscriptionSummary | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  /** `catalog`（カタログ価格へ戻す）か `manual`（金額を指定）か。 */
  const [mode, setMode] = React.useState<'catalog' | 'manual'>('manual');
  const [price, setPrice] = React.useState('');
  const [effectiveFrom, setEffectiveFrom] = React.useState('');
  const [reason, setReason] = React.useState('');

  const yearly = subscription?.billingPeriod === 'yearly';

  React.useEffect(() => {
    if (!subscription) return;
    setMode('manual');
    // 現在の契約金額は「単価 × 枠数」。単価に戻して初期値にする。
    setPrice(
      subscription.quantity > 0
        ? String(Math.round(subscription.amountJpy / subscription.quantity))
        : String(subscription.amountJpy),
    );
    setEffectiveFrom('');
    setReason('');
  }, [subscription]);

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/billing/subscriptions/${subscription?.id ?? ''}/reprice`, {
        reason: reason.trim(),
        ...(mode === 'catalog'
          ? { useCatalogPrice: true }
          : yearly
            ? { yearlyPrice: Number(price) }
            : { monthlyPrice: Number(price) }),
        ...(effectiveFrom ? { effectiveFrom } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions() });
      onClose();
    },
  });

  const cancelPending = useMutation({
    mutationFn: () =>
      api.post(`/billing/subscriptions/${subscription?.id ?? ''}/reprice`, {
        // 取り消しでも理由は必須（5 文字以上）。
        reason: '改定予約の取り消し（運営コンソール）',
        cancelPending: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions() });
      onClose();
    },
  });

  const current =
    subscription && subscription.quantity > 0
      ? Math.round(subscription.amountJpy / subscription.quantity)
      : (subscription?.amountJpy ?? 0);
  const next = mode === 'manual' ? Number(price) : NaN;
  /** 値上げか（＝効力発生日の指定が要るか）。カタログ価格は金額が分からないので促すだけ。 */
  const isIncrease = mode === 'manual' && Number.isFinite(next) && next > current;
  const invalid =
    reason.trim().length < 5 ||
    (mode === 'manual' && !(Number(price) >= 0)) ||
    (isIncrease && effectiveFrom.length === 0);

  return (
    <FormDialog
      open={subscription !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="単価を改定する"
      description={`${subscription?.organizationName ?? ''} の${yearly ? '年額' : '月額'}単価を変更します。`}
      submitLabel={effectiveFrom ? '改定を予約する' : '改定する'}
      pending={submit.isPending}
      error={submit.error ?? cancelPending.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      {subscription?.pendingPriceChange ? (
        <Alert variant="warning">
          <AlertDescription className="space-y-2">
            <p>
              {formatDateTime(subscription.pendingPriceChange.effectiveAt)} から{' '}
              {formatJpy(subscription.pendingPriceChange.amountJpy)} へ改定する予約が入っています。
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => cancelPending.mutate()}
              loading={cancelPending.isPending}
            >
              予約を取り消す
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <Field label="改定のしかた" required>
        <NativeSelect
          value={mode}
          onChange={(v) => setMode(v as 'catalog' | 'manual')}
          options={[
            { value: 'manual', label: '単価を指定する' },
            { value: 'catalog', label: 'カタログ価格に揃える（据え置き単価を破棄）' },
          ]}
        />
      </Field>

      {mode === 'manual' ? (
        <Field
          label={yearly ? '新しい年額単価（円・税抜）' : '新しい月額単価（円・税抜）'}
          required
          hint={`現在の単価は ${formatJpy(current)}（枠数 ${formatCount(subscription?.quantity ?? 0)}）です。`}
        >
          <Input
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
      ) : null}

      <Field
        label="効力発生日"
        required={isIncrease}
        hint="指定すると予約になり、その日まで現在の単価のままです。値上げでは必須です（30 日前までの通知を掲示しているため）。"
      >
        <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
      </Field>

      <Field label="理由" required hint="5 文字以上。契約履歴と監査ログに残ります。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>

      {isIncrease && effectiveFrom.length === 0 ? (
        <Alert variant="warning">
          <AlertDescription>
            値上げには効力発生日の指定が必要です。掲示（利用規約 第 11 条）では、不利益となる変更は
            効力発生日の 30 日前までに通知すると約束しています。
          </AlertDescription>
        </Alert>
      ) : null}
    </FormDialog>
  );
}
